import { FC } from "react";
import { Stack, Typography, Box, ButtonBase } from "@mui/material";
import { background, text, blue, red, black, secondary } from "@/theme/colors";
import { IconTrendUp, IconTrendDown, IconInfoOutline } from "@/assets/icons";
import { Tooltip } from "@/components/elements/Tooltip";

type TDashboardStatCardProps = {
  label: string;
  current: number;
  target?: number | null;
  previous?: number;
  unit: string;
  isTarget?: boolean;
  showDividerRight?: boolean;
  showIconArrowRight?: boolean;
  tooltipInfo?: string;
  isPrimaryLink?: boolean;
  // When provided AND current > 0, renders the current number as an underlined clickable button.
  // When current === 0 or handler omitted, renders as plain text (no underline).
  // Takes precedence over isPrimaryLink visual (which becomes redundant when set).
  onCurrentClick?: () => void;
  // null = no usable target, so the rate row is omitted (see the `rateCurrent != null` guard below).
  rateCurrent?: number | null;
  rateTarget?: number | null;
  ratePrevious?: number | null;
  rateUnit?: string;
};

export const DashboardStatCard: FC<TDashboardStatCardProps> = ({
  label,
  current,
  target,
  previous,
  unit,
  isTarget = false,
  showDividerRight = false,
  showIconArrowRight = false,
  tooltipInfo,
  isPrimaryLink = false,
  onCurrentClick,
  rateCurrent,
  rateTarget,
  ratePrevious,
  rateUnit = " %",
}) => {
  const difference = previous != null ? current - previous : null;
  // Rates use 1-decimal precision (same formula as /performance-detail/matching-statistics).
  // Round the subtraction to avoid floating-point tails like 0.899999... — IROHANA_MATCH_DEV-413
  const rateDifference = previous != null && ratePrevious != null && rateCurrent != null ? Math.round((rateCurrent - ratePrevious) * 10) / 10 : null;

  const targetDisplay = target != null ? target.toLocaleString() : "-";
  const currentDisplay = current.toLocaleString();

  // Click is only enabled when handler is set AND there's data to show (current > 0).
  // When current === 0, render plain text even if handler is provided (avoid dead underlines).
  const isClickable = !!onCurrentClick && current > 0;
  // Visual underline retained for legacy `isPrimaryLink` callers (decoration-only) when current > 0.
  const showUnderline = isClickable || (isPrimaryLink && current > 0);

  const getDifferenceColor = (diff: number) => {
    return diff > 0 ? blue[80] : diff < 0 ? red[85] : text.secondary;
  };

  return (
    <Stack
      gap={2}
      sx={{
        position: "relative",
        "::before": {
          content: '""',
          position: "absolute",
          top: "26px",
          right: 0,
          left: 0,
          borderTop: `1px solid ${background.disable}`,
        },
        ...(showDividerRight && {
          "::after": {
            content: '""',
            position: "absolute",
            right: "-40px",
            top: 0,
            bottom: 0,
            borderRight: `1px solid ${background.disable}`,
          },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Typography variant="cap12" color={text.secondary}>
          {label}
        </Typography>
        {tooltipInfo && (
          <Tooltip title={tooltipInfo}>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <IconInfoOutline width={16} height={16} color={black[80]} />
            </Box>
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack gap={0.5}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography component="span" variant="head20Bold" color={text.primary}>
              {showUnderline ? (
                <>
                  {isClickable ? (
                    <ButtonBase
                      onClick={onCurrentClick}
                      disableRipple
                      sx={{
                        p: 0,
                        verticalAlign: "baseline",
                        font: "inherit",
                        color: secondary[80],
                        textDecoration: "underline",
                        "&:hover": { opacity: 0.8 },
                      }}
                    >
                      {currentDisplay}
                    </ButtonBase>
                  ) : (
                    <Typography component="span" variant="head20Bold" sx={{ color: secondary[80], textDecoration: "underline" }}>
                      {currentDisplay}
                    </Typography>
                  )}
                  {isTarget && `/${targetDisplay}`}
                </>
              ) : (
                isTarget ? `${currentDisplay}/${targetDisplay}` : currentDisplay
              )}
            </Typography>

            <Stack direction="row">
              <Typography variant="head20Bold" color={text.primary}>
                {unit}
              </Typography>
              {isTarget && target == null && (
                <Tooltip title="目標値を設定してください。">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "start",
                      cursor: "pointer",
                      marginLeft: 1,
                    }}
                  >
                    <IconInfoOutline width={16} height={16} color={black[80]} />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </Stack>
          {rateCurrent != null && (
            <Typography variant="sub16Semi" color={text.primary}>
              ({rateCurrent}/{rateTarget != null ? rateTarget : "-"}{rateUnit})
            </Typography>
          )}
        </Stack>

        <Stack alignItems="flex-end" gap={0.5} pt={0.5}>
          {difference != null && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              {difference > 0 && <IconTrendUp />}
              {difference < 0 && <IconTrendDown />}
              <Typography variant="cap12" color={getDifferenceColor(difference)}>
                {difference > 0 ? "+" : ""}
                {difference.toLocaleString()}
              </Typography>
              <Typography variant="cap12" color={text.secondary}>
                前月比
              </Typography>
            </Stack>
          )}

          {rateDifference != null && (
            <Typography variant="cap12" color={getDifferenceColor(rateDifference)}>
              ({rateDifference > 0 ? "+" : ""}
              {rateDifference}{rateUnit})
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
